import {Drawer} from '@mantine/core';
import React, {createContext, useContext} from 'react';
import type {PillProps} from 'src/design/atoms/Pill';
import {ActionIcon} from 'src/design/atoms/ActionIcon';
import {Anchor} from 'src/design/atoms/Anchor';
import {Box} from 'src/design/atoms/Box';
import {Group} from 'src/design/atoms/Group';
import {LoadingOverlay} from 'src/design/atoms/LoadingOverlay';
import {Pill} from 'src/design/atoms/Pill';
import {ScrollArea} from 'src/design/atoms/ScrollArea';
import {Stack} from 'src/design/atoms/Stack';
import {SectionTitle} from 'src/design/atoms/Typography';
import {CloseIcon, IconSize} from 'src/design/atoms/icons/CommonIcons';

const DIVIDER = '1px solid var(--mantine-color-gray-3)';

const SideDrawerContext = createContext<{onClose: () => void}>({
  onClose: () => {},
});

export interface SideDrawerProps {
  opened: boolean;
  onClose: () => void;
  /** Drawer width. Defaults to 900px. */
  size?: string | number;
  'data-testid'?: string;
  children: React.ReactNode;
}

export interface SideDrawerHeaderProps {
  title: string;
  badges?: PillProps[];
  /** Opens the record's full page in a new tab, rendered as a "View Full Page" link. */
  fullPageHref?: string;
  /** Header-right content rendered before the close button (links, small buttons). */
  actions?: React.ReactNode;
}

export interface SideDrawerBodyProps {
  loading?: boolean;
  children?: React.ReactNode;
}

export interface SideDrawerFooterProps {
  children: React.ReactNode;
}

const SideDrawerHeader: React.FC<SideDrawerHeaderProps> = ({
  title,
  badges,
  fullPageHref,
  actions,
}) => {
  const {onClose} = useContext(SideDrawerContext);
  return (
    <Box p="md" style={{borderBottom: DIVIDER}}>
      <Group justify="space-between" align="flex-start" wrap="nowrap">
        <Stack gap={4} flex={1} miw={0}>
          <SectionTitle text={title} />
          {badges && badges.length > 0 && (
            <Group gap="xs">
              {badges.map(pill => (
                <Pill key={pill.text} text={pill.text} color={pill.color} />
              ))}
            </Group>
          )}
        </Stack>
        <Group gap="xs" wrap="nowrap" style={{flexShrink: 0}}>
          {fullPageHref && (
            <Anchor
              size="xs"
              href={fullPageHref}
              target="_blank"
              style={{whiteSpace: 'nowrap'}}>
              View Full Page
            </Anchor>
          )}
          {actions}
          <ActionIcon
            tooltipText="Close"
            size="md"
            color="black"
            onClick={onClose}>
            <CloseIcon size={IconSize.lg} />
          </ActionIcon>
        </Group>
      </Group>
    </Box>
  );
};
SideDrawerHeader.displayName = 'SideDrawer.Header';

const SideDrawerBody: React.FC<SideDrawerBodyProps> = ({loading, children}) => (
  <ScrollArea flex={1} p="md" scrollbars="y">
    <Box pos="relative" mih={200} maw="100%">
      <LoadingOverlay visible={!!loading} />
      {children}
    </Box>
  </ScrollArea>
);
SideDrawerBody.displayName = 'SideDrawer.Body';

const SideDrawerFooter: React.FC<SideDrawerFooterProps> = ({children}) => (
  <Box p="md" style={{borderTop: DIVIDER}}>
    {children}
  </Box>
);
SideDrawerFooter.displayName = 'SideDrawer.Footer';

/**
 * Right-hand slide-out drawer with a fixed header, scrolling body and bordered footer.
 * @registry Side preview/edit drawer (Header with title/pills/actions/close, scroll Body, Footer bar); compound API.
 */
const SideDrawerRoot: React.FC<SideDrawerProps> = ({
  opened,
  onClose,
  size = '900px',
  'data-testid': testId,
  children,
}) => (
  <Drawer
    data-testid={testId}
    opened={opened}
    onClose={onClose}
    position="right"
    size={size}
    withCloseButton={false}
    padding={0}>
    <SideDrawerContext.Provider value={{onClose}}>
      <Stack h="100vh" gap={0}>
        {children}
      </Stack>
    </SideDrawerContext.Provider>
  </Drawer>
);
SideDrawerRoot.displayName = 'SideDrawer';

export const SideDrawer = Object.assign(SideDrawerRoot, {
  Header: SideDrawerHeader,
  Body: SideDrawerBody,
  Footer: SideDrawerFooter,
});
